/**
 * ============================================================================
 * 동기화 WebSocket 훅 (useSyncSocket)
 * ============================================================================
 * 
 * 주요 기능 요약
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 1. WebSocket 연결 관리
 * 2. 교수자: sync/start 메시지 전송
 * 3. 학생: sync/start 메시지 수신 및 처리
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 메시지 형식
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * sync/start (교수자 → 학생):
 * {
 *   "cmd": "sync/start",
 *   "type": "req",
 *   "trid": "unique-transaction-id",
 *   "data": { "page_no": 37 }
 * }
 * 
 * ============================================================================
 */

import { useState, useCallback, useRef, useEffect } from 'react';

// ============================================================================
// 타입 정의
// ============================================================================

interface SyncMessage {
  cmd: string;
  type: string;
  trid: string;
  data: {
    page_no?: number;
    join_code?: string;
    success?: boolean;
    error?: string | null;
    data?: {
      page_no?: number;
      quizzes?: Array<{
        question: string;
        answer: string;
        explanation: string;
      }>;
      // quiz/publish 메시지의 단일 퀴즈 객체 구조
      question?: string;
      quiz_uuid?: string;
      delivery_uuid?: string;
      set_uuid?: string;
      set_rev_no?: number;
      quiz_no?: number;
      item_idx?: number;
      choices?: any;
      correct_answer?: string;
      hint?: string;
      difficulty?: string;
      scope?: string;
      expires_at?: string;
    } | Array<any> | null;
    quizzes?: Array<{
      question: string;
      answer: string;
      explanation: string;
    }>;
    code?: string | null;
  };
}

interface SyncRequest {
  pageNo: number;
  trid: string;
  timestamp: Date;
}

interface QuizPublishData {
  quizzes: Array<{
    question: string;
    answer: string;
    explanation: string;
  }>;
}

// 공유 폴더 발송 데이터 타입
interface SharedFolderPublishData {
  folder_uuid: string;
  book_uuid: string;
  title: string;
}

interface UseSyncSocketOptions {
  wsUrl: string | null;
  joinCode?: string;
  isProfessor: boolean;
  token?: string | null;
  roomUuid?: string | null;
  onSyncRequest?: (request: SyncRequest) => void;
  onQuizPublish?: (data: QuizPublishData) => void;
  onSharedFolderPublish?: (data: SharedFolderPublishData) => void;
}

interface QuizItem {
  id: string;
  questionNumber: number;
  question: string;
  answer: string;
  explanation: string;
  selected: boolean;
}

interface UseSyncSocketReturn {
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
  sendSyncStart: (pageNo: number) => boolean;
  sendQuizPublish: (quizzes: QuizItem[], setUuid: string, setRevNo: number) => void;
  sendSharedFolderPublish: (folderUuid: string, bookUuid: string, title: string) => void;
  pendingSyncRequest: SyncRequest | null;
  clearPendingRequest: () => void;
  wasAbnormalDisconnect: boolean;
}

// ============================================================================
// 훅 구현
// ============================================================================

const useSyncSocket = ({
  wsUrl,
  joinCode,
  isProfessor,
  token,
  roomUuid,
  onSyncRequest,
  onQuizPublish,
  onSharedFolderPublish,
}: UseSyncSocketOptions): UseSyncSocketReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [pendingSyncRequest, setPendingSyncRequest] = useState<SyncRequest | null>(null);
  const [wasAbnormalDisconnect, setWasAbnormalDisconnect] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 콜백 refs - 최신 콜백을 항상 참조하도록 함
  const onSyncRequestRef = useRef(onSyncRequest);
  const onQuizPublishRef = useRef(onQuizPublish);
  const onSharedFolderPublishRef = useRef(onSharedFolderPublish);
  // handleMessage ref - 소켓 연결 후에도 최신 핸들러를 사용하기 위함
  const handleMessageRef = useRef<(event: MessageEvent) => void>(() => {});

  // 콜백 refs 업데이트
  useEffect(() => {
    onSyncRequestRef.current = onSyncRequest;
  }, [onSyncRequest]);

  useEffect(() => {
    onQuizPublishRef.current = onQuizPublish;
  }, [onQuizPublish]);

  useEffect(() => {
    onSharedFolderPublishRef.current = onSharedFolderPublish;
  }, [onSharedFolderPublish]);

  // 고유 트랜잭션 ID 생성
  const generateTrid = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // 메시지 수신 핸들러
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const rawData = event.data;
      const message: SyncMessage = JSON.parse(rawData);
      
      // 학생인 경우 모든 메시지를 상세히 로깅
      if (!isProfessor) {
        console.log('[SyncSocket] ===== 학생 메시지 수신 =====', {
          cmd: message.cmd,
          type: message.type,
          trid: message.trid,
          rawData: rawData,
          parsedMessage: message,
        });
      }

      // join 응답 처리
      if (message.cmd === 'join' && message.type === 'res') {
        if (message.data.success) {
          console.log('[SyncSocket] join 성공:', {
            isProfessor,
            joinCode,
            message: message.data,
          });
        } else {
          console.error('[SyncSocket] join 실패:', {
            error: message.data.error,
            isProfessor,
            joinCode,
            message: message.data,
          });
        }
        return;
      }

      // 학생: sync/start 메시지 처리 (push 타입으로 broadcast됨)
      if (!isProfessor && message.cmd === 'sync/start' && message.type === 'push') {
        // 서버 응답 구조: data.data.page_no (중첩된 data 안에 있음)
        const dataData = message.data.data as any;
        const pageNo = (dataData && typeof dataData === 'object' && !Array.isArray(dataData) ? dataData.page_no : undefined) ?? message.data.page_no ?? 0;
        const request: SyncRequest = {
          pageNo: pageNo,
          trid: message.trid,
          timestamp: new Date(),
        };
        console.log('[SyncSocket] 동기화 요청 수신:', request);
        setPendingSyncRequest(request);
        onSyncRequestRef.current?.(request);
        return;
      }

      // 학생: quiz/publish 메시지 처리 (push 타입으로 broadcast됨 - sync/start와 동일)
      if (!isProfessor && message.cmd === 'quiz/publish' && message.type === 'push') {
        // console.log('[SyncSocket] quiz/publish 메시지 수신 (push):', {
        //   cmd: message.cmd,
        //   type: message.type,
        //   trid: message.trid,
        //   data: message.data,
        //   fullMessage: message,
        // });

        // 서버 응답 구조 확인
        // 구조 1: data.data가 배열인 경우 (여러 퀴즈)
        // 구조 2: data.data가 객체인 경우 (단일 퀴즈)
        // 구조 3: data.data.quizzes가 배열인 경우
        // 구조 4: data.quizzes가 배열인 경우
        let quizzes: any[] = [];
        
        const dataData = message.data.data as any; // 유연한 타입 처리
        
        if (dataData) {
          if (Array.isArray(dataData)) {
            // data.data가 배열인 경우
            quizzes = dataData;
          } else if (dataData.quizzes && Array.isArray(dataData.quizzes)) {
            // data.data.quizzes가 배열인 경우
            quizzes = dataData.quizzes;
          } else if (dataData.question || dataData.quiz_uuid) {
            // data.data가 단일 퀴즈 객체인 경우 (배열로 변환)
            quizzes = [dataData];
          }
        } else if (message.data.quizzes && Array.isArray(message.data.quizzes)) {
          // data.quizzes가 배열인 경우
          quizzes = message.data.quizzes;
        }
        
        const dataDataForLog = message.data.data as any;
        // console.log('[SyncSocket] 퀴즈 데이터 추출:', {
        //   quizzesCount: quizzes.length,
        //   quizzes: quizzes,
        //   dataData: message.data.data,
        //   data: message.data,
        //   isDataArray: Array.isArray(dataDataForLog),
        //   isDataObject: dataDataForLog && typeof dataDataForLog === 'object' && !Array.isArray(dataDataForLog),
        //   hasQuizzes: !!message.data.quizzes,
        //   hasDataQuizzes: !!(dataDataForLog && dataDataForLog.quizzes),
        // });
        
        if (quizzes.length > 0) {
          console.log('[SyncSocket] 퀴즈 발송 수신 성공:', { quizzesCount: quizzes.length });
          onQuizPublishRef.current?.({ quizzes });
        } else {
          // console.warn('[SyncSocket] 퀴즈 배열이 비어있음:', {
          //   data: message.data,
          //   dataData: message.data.data,
          //   hasQuizzes: !!message.data.quizzes,
          //   hasDataQuizzes: !!(dataData && dataData.quizzes),
          // });
        }
        return;
      }

      // 학생: shared-folder/publish 메시지 처리 (push 타입으로 broadcast됨)
      if (!isProfessor && message.cmd === 'shared-folder/publish' && message.type === 'push') {
        // console.log('[SyncSocket] shared-folder/publish 메시지 수신 (push):', {
        //   cmd: message.cmd,
        //   type: message.type,
        //   trid: message.trid,
        //   data: message.data,
        // });

        // 서버 응답 구조: data.data 또는 data에서 직접 추출
        const dataData = message.data.data as any;
        const folderData: SharedFolderPublishData = {
          folder_uuid: dataData?.folder_uuid || (message.data as any).folder_uuid || '',
          book_uuid: dataData?.book_uuid || (message.data as any).book_uuid || '',
          title: dataData?.title || (message.data as any).title || '공유 폴더',
        };

        if (folderData.folder_uuid) {
          console.log('[SyncSocket] 공유 폴더 발송 수신 성공:', folderData);
          onSharedFolderPublishRef.current?.(folderData);
        } else {
          // console.warn('[SyncSocket] 공유 폴더 데이터가 유효하지 않음:', {
          //   data: message.data,
          //   dataData: message.data.data,
          // });
        }
        return;
      }

      // 처리되지 않은 메시지 로깅 (디버깅용)
      if (!isProfessor) {
        // console.log('[SyncSocket] 처리되지 않은 메시지:', {
        //   cmd: message.cmd,
        //   type: message.type,
        //   data: message.data,
        // });
      }
    } catch (error) {
      // console.error('[SyncSocket] 메시지 파싱 실패:', error, event.data);
    }
  }, [isProfessor, joinCode]);

  // handleMessageRef 업데이트 - 소켓 연결 후에도 최신 핸들러 사용
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  // WebSocket 연결
  const connect = useCallback(() => {
    // console.log('[SyncSocket] connect 함수 호출됨:', {
    //   wsUrl,
    //   joinCode,
    //   isProfessor,
    //   hasToken: !!token,
    //   role: isProfessor ? '교수자' : '학생',
    // });

    if (!wsUrl) {
      // console.error('[SyncSocket] WebSocket URL이 없습니다. 연결 중단.');
      return;
    }

    // 이미 연결되어 있거나 연결 중이면 무시
    if (socketRef.current) {
      const state = socketRef.current.readyState;
      const stateNames = {
        [WebSocket.CONNECTING]: 'CONNECTING',
        [WebSocket.OPEN]: 'OPEN',
        [WebSocket.CLOSING]: 'CLOSING',
        [WebSocket.CLOSED]: 'CLOSED',
      };
      // console.log('[SyncSocket] 기존 소켓 상태 확인:', {
      //   state: stateNames[state] || state,
      //   stateCode: state,
      // });
      
      if (state === WebSocket.CONNECTING || state === WebSocket.OPEN) {
        // console.log('[SyncSocket] 이미 연결되어 있거나 연결 중입니다. 연결 중단.');
        return;
      }
      // CLOSING 또는 CLOSED 상태면 정리
      // console.log('[SyncSocket] 기존 소켓이 닫힌 상태입니다. 정리 후 재연결.');
      socketRef.current = null;
    }

    // URL에 쿼리 파라미터 추가 (token, join_code)
    const urlObj = new URL(wsUrl);
    
    // 인증 토큰 추가 (교수자/학생 모두)
    if (token) {
      urlObj.searchParams.set('token', token);
      // console.log('[SyncSocket] 토큰 추가됨 (길이:', token.length, ')');
    } else {
      // console.warn('[SyncSocket] 토큰이 없습니다!');
    }
    
    // 학생인 경우 join_code 추가
    if (!isProfessor && joinCode) {
      urlObj.searchParams.set('join_code', joinCode);
      // console.log('[SyncSocket] join_code 추가됨:', joinCode);
    } else if (!isProfessor && !joinCode) {
      // console.warn('[SyncSocket] 학생인데 join_code가 없습니다!');
    }

    const connectionUrl = urlObj.toString();
      // console.log('[SyncSocket] 연결 시도 시작:', {
      //   connectionUrl,
      //   isProfessor,
      //   hasJoinCode: !!joinCode,
      //   hasToken: !!token,
      // });

    const socket = new WebSocket(connectionUrl);
    // console.log('[SyncSocket] WebSocket 객체 생성 완료, 이벤트 리스너 등록 중...');

    socket.onopen = (event) => {
      console.log('[SyncSocket] ===== onopen 이벤트 발생 =====', {
        role: isProfessor ? '교수자' : '학생',
        readyState: socket.readyState,
        url: socket.url,
        protocol: socket.protocol,
        extensions: socket.extensions,
      });
      
      // 연결 후 join 메시지 전송 (필수!)
      const joinMessage = {
        cmd: 'join',
        type: 'req',
        trid: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        data: isProfessor ? {} : { join_code: joinCode },
      };
      console.log('[SyncSocket] join 메시지 전송:', {
        message: joinMessage,
        role: isProfessor ? '교수자' : '학생',
      });
      
      try {
        socket.send(JSON.stringify(joinMessage));
        console.log('[SyncSocket] join 메시지 전송 완료');
      } catch (error) {
        console.error('[SyncSocket] join 메시지 전송 실패:', error);
      }
      
      setIsConnected(true);
      setWasAbnormalDisconnect(false); // 정상 연결 시 비정상 종료 플래그 리셋
      console.log('[SyncSocket] 연결 상태 업데이트: isConnected = true');
    };

    socket.onclose = (event) => {
      // console.log('[SyncSocket] ===== onclose 이벤트 발생 =====', {
      //   code: event.code,
      //   reason: event.reason,
      //   wasClean: event.wasClean,
      //   role: isProfessor ? '교수자' : '학생',
      //   codeMeaning: {
      //     1000: '정상 종료',
      //     1001: '엔드포인트가 떠남',
      //     1002: '프로토콜 오류',
      //     1003: '지원하지 않는 데이터 타입',
      //     1006: '비정상 종료 (코드 없음)',
      //     1007: '데이터 형식 오류',
      //     1008: '정책 위반',
      //     1009: '메시지가 너무 큼',
      //     1010: '확장 협상 실패',
      //     1011: '서버 오류',
      //   }[event.code] || '알 수 없음',
      // });
      
      // 1000 = 정상 종료 코드, 그 외는 비정상 종료
      const isAbnormal = event.code !== 1000;
      
      if (isAbnormal) {
        // console.warn('[SyncSocket] 비정상 종료 감지:', {
        //   code: event.code,
        //   reason: event.reason,
        //   wasClean: event.wasClean,
        //   role: isProfessor ? '교수자' : '학생',
        // });
        setWasAbnormalDisconnect(true);
      } else {
        // console.log('[SyncSocket] 정상 종료:', event.code, event.reason);
        setWasAbnormalDisconnect(false);
      }
      
      setIsConnected(false);
      socketRef.current = null;
      // console.log('[SyncSocket] 연결 상태 업데이트: isConnected = false');
    };

    socket.onerror = (error) => {
      // console.error('[SyncSocket] ===== onerror 이벤트 발생 =====', {
      //   error,
      //   role: isProfessor ? '교수자' : '학생',
      //   readyState: socket.readyState,
      //   url: socket.url,
      // });
      // console.warn('[SyncSocket] 비정상 종료 감지 (에러):', {
      //   role: isProfessor ? '교수자' : '학생',
      // });
      setWasAbnormalDisconnect(true);
      setIsConnected(false);
    };

    // 초기 onmessage 설정 - handleMessageRef를 통해 최신 핸들러 사용
    socket.onmessage = (event) => {
      handleMessageRef.current(event);
    };

    socketRef.current = socket;
    console.log('[SyncSocket] 소켓 참조 저장 완료, 연결 대기 중...');
  }, [wsUrl, joinCode, isProfessor, token, roomUuid]);

  // WebSocket 연결 해제
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      // 1000 = 정상 종료 코드
      socketRef.current.close(1000, 'User disconnected');
      socketRef.current = null;
      setWasAbnormalDisconnect(false); // 명시적 종료는 정상 종료
    }

    setIsConnected(false);
    setPendingSyncRequest(null);
  }, []);

  // 교수자: sync/start 메시지 전송
  // 반환값: true = 전송 성공, false = 전송 실패
  const sendSyncStart = useCallback((pageNo: number): boolean => {
    if (!socketRef.current) {
      console.error('[SyncSocket] WebSocket 객체가 없습니다.');
      return false;
    }
    
    if (socketRef.current.readyState !== WebSocket.OPEN) {
      console.error('[SyncSocket] WebSocket이 연결되어 있지 않습니다. readyState:', socketRef.current.readyState);
      return false;
    }

    const message = {
      cmd: 'sync/start',
      type: 'req',
      trid: generateTrid(),
      data: {
        page_no: pageNo,
      },
    };

    try {
      socketRef.current.send(JSON.stringify(message));
      console.log('[SyncSocket] sync/start 전송 성공:', { pageNo, trid: message.trid });
      return true;
    } catch (error) {
      console.error('[SyncSocket] sync/start 전송 실패:', error);
      return false;
    }
  }, [generateTrid]);

  // 교수자: 퀴즈 발송 메시지 전송
  const sendQuizPublish = useCallback((quizzes: QuizItem[], setUuid: string, setRevNo: number) => {
    // console.log('[SyncSocket] quiz/publish 호출:', {
    //   hasSocket: !!socketRef.current,
    //   socketState: socketRef.current?.readyState,
    //   isConnected,
    //   quizzesCount: quizzes.length,
    //   setUuid,
    //   setRevNo,
    // });

    if (!socketRef.current) {
      // console.error('[SyncSocket] socketRef.current가 null입니다.');
      alert('동기화 소켓이 연결되어 있지 않습니다. 먼저 동기화를 시작해주세요.');
      return;
    }

    if (socketRef.current.readyState !== WebSocket.OPEN) {
      // console.error('[SyncSocket] WebSocket 상태가 OPEN이 아닙니다:', socketRef.current.readyState);
      alert('동기화 소켓이 연결되어 있지 않습니다. 먼저 동기화를 시작해주세요.');
      return;
    }

    if (!setUuid) {
      // console.error('[SyncSocket] setUuid가 없습니다.');
      alert('퀴즈 세트 정보가 없습니다. 먼저 퀴즈를 생성해주세요.');
      return;
    }

    try {
      const currentSocket = socketRef.current;
      if (!currentSocket) {
        // console.error('[SyncSocket] socketRef.current가 null입니다.');
        return;
      }

      // 각 퀴즈를 개별 메시지로 전송
      quizzes.forEach((quiz, index) => {
        const trid = generateTrid();
        const message = {
          cmd: 'quiz/publish',
          type: 'req',
          trid: trid,
          data: {
            set_uuid: setUuid, // 퀴즈 생성 API에서 받은 set_uuid 사용
            set_rev_no: setRevNo, // 퀴즈 생성 API에서 받은 set_rev_no 사용
            quiz_uuid: quiz.id || `quiz-${Date.now()}-${index}`, // QuizItem.id 사용
            item_idx: quiz.questionNumber || index + 1, // questionNumber 또는 인덱스 사용
            audience: 'students',
            delivery_meta: {
              expires_in_sec: 120,
            },
          },
        };

        // console.log('[SyncSocket] quiz/publish 전송:', message);
        currentSocket.send(JSON.stringify(message));
      });
      
      // console.log('[SyncSocket] quiz/publish 전송 완료:', { count: quizzes.length });
    } catch (error) {
      // console.error('[SyncSocket] quiz/publish 전송 실패:', error);
      alert('퀴즈 발송 중 오류가 발생했습니다: ' + (error instanceof Error ? error.message : String(error)));
    }
  }, [generateTrid, isConnected]);

  // sendQuizPublish가 항상 정의되도록 보장
  if (!sendQuizPublish) {
    // console.warn('[SyncSocket] sendQuizPublish가 정의되지 않았습니다.');
  }

  // 교수자: 공유 폴더 발송 메시지 전송
  const sendSharedFolderPublish = useCallback((folderUuid: string, bookUuid: string, title: string) => {
    // console.log('[SyncSocket] shared-folder/publish 호출:', {
    //   hasSocket: !!socketRef.current,
    //   socketState: socketRef.current?.readyState,
    //   isConnected,
    //   folderUuid,
    //   bookUuid,
    //   title,
    // });

    if (!socketRef.current) {
      // console.error('[SyncSocket] socketRef.current가 null입니다.');
      alert('동기화 소켓이 연결되어 있지 않습니다. 먼저 동기화를 시작해주세요.');
      return;
    }

    if (socketRef.current.readyState !== WebSocket.OPEN) {
      // console.error('[SyncSocket] WebSocket 상태가 OPEN이 아닙니다:', socketRef.current.readyState);
      alert('동기화 소켓이 연결되어 있지 않습니다. 먼저 동기화를 시작해주세요.');
      return;
    }

    if (!folderUuid) {
      // console.error('[SyncSocket] folderUuid가 없습니다.');
      alert('폴더 정보가 없습니다.');
      return;
    }

    try {
      const message = {
        cmd: 'shared-folder/publish',
        type: 'req',
        trid: generateTrid(),
        data: {
          folder_uuid: folderUuid,
          book_uuid: bookUuid,
          title: title,
        },
      };

      // console.log('[SyncSocket] shared-folder/publish 전송:', message);
      socketRef.current.send(JSON.stringify(message));
      // console.log('[SyncSocket] shared-folder/publish 전송 완료');
    } catch (error) {
      // console.error('[SyncSocket] shared-folder/publish 전송 실패:', error);
      alert('공유 폴더 전송 중 오류가 발생했습니다: ' + (error instanceof Error ? error.message : String(error)));
    }
  }, [generateTrid, isConnected]);

  // 대기 중인 요청 초기화
  const clearPendingRequest = useCallback(() => {
    setPendingSyncRequest(null);
  }, []);

  // wsUrl 변경 시 기존 연결 정리
  useEffect(() => {
    // wsUrl이 없으면 기존 연결 정리만
    if (!wsUrl) {
      if (socketRef.current) {
        // console.log('[SyncSocket] wsUrl 없음, 기존 연결 정리');
        socketRef.current.close(1000, 'URL cleared');
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    // wsUrl이 변경되었고 현재 다른 URL로 연결되어 있다면 정리
    if (socketRef.current && socketRef.current.url !== wsUrl && !socketRef.current.url.startsWith(wsUrl)) {
      // console.log('[SyncSocket] wsUrl 변경 감지, 기존 연결 정리:', socketRef.current.url);
      socketRef.current.close(1000, 'URL changed');
      socketRef.current = null;
      setIsConnected(false);
    }
  }, [wsUrl]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    connect,
    disconnect,
    sendSyncStart,
    sendQuizPublish,
    sendSharedFolderPublish,
    pendingSyncRequest,
    clearPendingRequest,
    wasAbnormalDisconnect,
  };
};

export default useSyncSocket;
export type { SyncRequest, UseSyncSocketReturn, SharedFolderPublishData };


